import { Tour } from '../types';
import { appConfig } from '../config/appConfig';

// Helper function to get a random logo from config
const getRandomLogo = (): string => {
  const randomIndex = Math.floor(Math.random() * appConfig.fallbackLogos.length);
  return appConfig.fallbackLogos[randomIndex];
};

// Helper function to get admin token
const getAdminToken = (): string => {
  return btoa(`${appConfig.admin.username}:${appConfig.admin.password}`);
};

// API response interface for tours
interface ToursResponse {
  items: Tour[];
  nextCursor: string | null;
}

// Transform backend tour data to frontend Tour interface
const transformTourData = (backendTour: any): Tour => {
  // Helper function to convert Firestore timestamp to ISO string
  const convertTimestamp = (timestamp: any): string => {
    if (!timestamp) return new Date().toISOString();
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000).toISOString();
    }
    return new Date(timestamp).toISOString();
  };

  // Use random logo if no banner is provided
  const imageUrl = backendTour.banner || getRandomLogo();

  return {
    tourId: backendTour.id,
    tourName: backendTour.name,
    tagline: typeof backendTour.tagline === 'string' ? backendTour.tagline : '',
    destination: backendTour.countries ? backendTour.countries.join(', ') : (backendTour.location || ''),
    countries: Array.isArray(backendTour.countries) ? backendTour.countries : [],
    departureDate: convertTimestamp(backendTour.departure),
    returnDate: convertTimestamp(backendTour.arrival),
    deadline: convertTimestamp(backendTour.deadline),
    durationDays: backendTour.durationDays || 1,
    durationNights: backendTour.durationNights || 0,
    price: backendTour.price,
    description: backendTour.description || '',
    maxGroupSize: typeof backendTour.groupSize === 'number' ? backendTour.groupSize : undefined,
    availableSlots: typeof backendTour.availability === 'number' ? backendTour.availability : undefined,
    urgencySlots: typeof backendTour.urgencySlots === 'number' ? backendTour.urgencySlots : undefined,
    imageUrl,
    brochureUrl: backendTour.brochure,
    isNew: backendTour.isNew || false,
    createdAt: convertTimestamp(backendTour.createdAt),
    updatedAt: convertTimestamp(backendTour.updatedAt),
    status: backendTour.status || 'active'
  };
};

// Fetch tours from the backend API
export const fetchTours = async (limit: number = 10): Promise<Tour[]> => {
  try {
    const response = await fetch(`/api/tours?limit=${limit}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data: ToursResponse = await response.json();
    
    // Transform backend data to frontend format
    const tours = data.items.map(transformTourData);
    
    // Sort tours by departure date in chronological order (nearest first)
    tours.sort((a, b) => {
      const dateA = new Date(a.departureDate).getTime();
      const dateB = new Date(b.departureDate).getTime();
      return dateA - dateB;
    });
    
    return tours;
  } catch (error) {
    console.error('Error fetching tours:', error);
    throw error;
  }
};

// Fetch a single tour by ID
export const fetchTourById = async (tourId: string): Promise<Tour | null> => {
  try {
    const response = await fetch(`/api/tours/${tourId}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return transformTourData(data);
  } catch (error) {
    console.error('Error fetching tour:', error);
    throw error;
  }
};

// Fetch all tours for admin (regardless of status)
export const fetchAllToursForAdmin = async (): Promise<Tour[]> => {
  try {
    const response = await fetch('/api/tours?limit=50&includeAll=true', {
      headers: {
        'Authorization': `Bearer ${getAdminToken()}`,
        'Accept': 'application/json',
      },
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed. Please check admin credentials.');
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data: ToursResponse = await response.json();
    
    // Transform backend data to frontend format
    return data.items.map(transformTourData);
  } catch (error) {
    console.error('Error fetching all tours for admin:', error);
    throw error;
  }
};

// Update a tour (admin only) - now accepts FormData for file uploads
export const updateTour = async (tourId: string, tourData: FormData, adminToken: string): Promise<Tour> => {
  try {
    const response = await fetch(`/api/tours/${tourId}`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        // Don't set Content-Type header - let browser set it for FormData
      },
      body: tourData,
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    return transformTourData(data);
  } catch (error) {
    console.error('Error updating tour:', error);
    throw error;
  }
};

// Delete a tour (admin only)
export const deleteTour = async (tourId: string, adminToken: string): Promise<void> => {
  try {
    const response = await fetch(`/api/tours/${tourId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${adminToken}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    console.error('Error deleting tour:', error);
    throw error;
  }
};
